import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Table,
  Tag,
  Typography,
  PLATFORM_THEME_COLORS,
  Popover,
  Icon,
  ICONS,
} from '@thinknet-gmbh/platform-common-ui';
import { AppraisalInterviewStatusesEnum } from '@thinknet-gmbh/platform-common';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { EMPLOYEE_PAGES } from 'src/pages/employee/employee.constants';
import { APPRAISAL_INTERVIEW_STATUSES_STYLES } from 'src/pages/employee/sub-pages/employee-interview/employee-interview.constants';

import { IAppraisalInterviewTableProps } from './employee-interview-list-table.interfaces';
import { TextContainer } from './employee-interview-list-table.styles';

const EmployeeInterviewListTable = ({
  dataSource,
  paginationConfig,
  onTableChange,
  employeeId,
}: IAppraisalInterviewTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'employee']);

  const navigateToAppraisalDetails = (appraisalId: string) =>
    navigate(
      `${ROUTES.employee}?type=${EMPLOYEE_PAGES.appraisalInterviewDetails}&id=${employeeId}&appraisalInterviewId=${appraisalId}`
    );

  return (
    <Table
      t={t}
      dataSource={dataSource}
      rowKey={record => record._id}
      pagination={paginationConfig}
      onChange={onTableChange}
      onRow={record => ({
        onClick: () => navigateToAppraisalDetails(record._id),
      })}
    >
      <Table.Column
        title={t('employee:employeeInterview.labels.employeeInterviewName')}
        key="interviewName"
        dataIndex="interviewName"
        render={name => (
          <Typography>
            <TextContainer>{name || '-'}</TextContainer>
          </Typography>
        )}
      />
      <Table.Column
        title={t('employee:employeeInterview.labels.interviewType')}
        key="interviewType"
        dataIndex="interviewType"
        render={({ name }) => <Typography>{name}</Typography>}
      />
      <Table.Column
        title={t('employee:labels.createdBy')}
        dataIndex={['createdByMember', 'profile']}
        key="createdBy"
        render={({ displayName }) => <Typography>{displayName}</Typography>}
      />
      <Table.Column
        title={t('employee:employeeInterview.labels.setTo')}
        key="setTo"
        dataIndex="setTo"
        render={setTo => <Typography>{moment(setTo).format('DD.MM.YYYY')}</Typography>}
      />
      <Table.Column
        title={t('employee:employeeInterview.labels.feedback')}
        key="feedback"
        dataIndex="feedback"
        render={feedback => (
          <Typography>
            <TextContainer>{feedback || '-'}</TextContainer>
          </Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.status')}
        key="status"
        dataIndex="status"
        render={status => {
          const { background, fontColor } =
            APPRAISAL_INTERVIEW_STATUSES_STYLES[status as AppraisalInterviewStatusesEnum];

          return (
            <Tag background={background} fontColor={fontColor}>
              {t(`employee:employeeInterview.statuses.${status.toLowerCase()}`).toLowerCase()}
            </Tag>
          );
        }}
      />
      <Table.Column
        title={t('common:labels.creationDate')}
        key="createdAt"
        dataIndex="createdAt"
        sorter
        render={createdAt => <Typography>{moment(createdAt).format('DD.MM.YYYY')}</Typography>}
      />
      <Table.Column
        width={50}
        title={t('common:labels.action')}
        key="_id"
        dataIndex="_id"
        render={id => (
          <Popover
            elements={[
              {
                label: t('employee:labels.viewDetailsAndEdit'),
                key: ICONS.foodMenu,
                icon: ICONS.foodMenu,
                handler: () => navigateToAppraisalDetails(id),
              },
            ]}
          >
            <Icon
              icon={ICONS.dotsVerticalRounded}
              color={PLATFORM_THEME_COLORS.grayVariations.dark}
              size={22}
              rightMargin={false}
            />
          </Popover>
        )}
      />
    </Table>
  );
};

export default EmployeeInterviewListTable;
